// src/hooks/useMachineDetail.js
import {useCallback, useEffect, useMemo, useState} from "react";
import {fetchJson} from "../services/apiClient";

function isValidYmd(dateStr) {
  // Validate YYYY-MM-DD without accepting invalid dates like 2025-13-40.
  if (typeof dateStr !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}` === dateStr;
}

export function useMachineDetail(machineSerialNumber, dateYmd) {
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const canFetch = useMemo(() => {
    return Boolean(machineSerialNumber) && isValidYmd(dateYmd);
  }, [machineSerialNumber, dateYmd]);

  const fetchDetail = useCallback(async () => {
    if (!canFetch) return null;

    const data = await fetchJson(`/api/ui/machine/${encodeURIComponent(machineSerialNumber)}?date=${dateYmd}`);

    const m = data?.machine ?? null;

    // Optional normalization: keep legacy UI fields if backend returns latestJob/latestStatus.
    if (m && (m.latestJob !== undefined || m.latestStatus !== undefined)) {
      return {
        ...m,
        job: m.job ?? m.latestJob ?? "-",
        status: m.status ?? m.latestStatus ?? "Inactive",
      };
    }

    return m;
  }, [canFetch, machineSerialNumber, dateYmd]);

  useEffect(() => {
    let mounted = true;

    async function loadInitial() {
      try {
        setLoading(true);
        setError(null);

        const m = await fetchDetail();
        if (!mounted) return;

        setMachine(m);
      } catch (err) {
        console.error("Error loading machine detail:", err);
        if (!mounted) return;

        setMachine(null);
        setError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadInitial();

    return () => {
      mounted = false;
    };
  }, [fetchDetail]);

  useEffect(() => {
    if (!canFetch) return;

    // Refresh detail every 60 seconds (no loading flicker).
    const intervalId = setInterval(() => {
      fetchDetail()
        .then((m) => setMachine(m))
        .catch((err) => {
          console.error("Error refreshing machine detail:", err);
          setError(err);
        });
    }, 60000);

    return () => clearInterval(intervalId);
  }, [canFetch, fetchDetail]);

  return {machine, loading, error, refresh: fetchDetail};
}
